<template>
    <div>
        <h2
            v-t="'Direkte Abteilungszugehörigkeiten'"
            class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
        ></h2>

        <table class="kgr-table kgr-table--striped table-groups kgr-row kgr-table--block">
            <thead>
                <tr class="kgr-table__row">
                    <th
                        v-t="'department'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Start'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th
                        v-t="'Ende'"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                    <th class="kgr-table__cell kgr-table__cell--headline"></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="departmentRelation in departmentMemberRelations"
                    :key="departmentRelation.id"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        {{ departmentRelation.department.name }}
                        <div
                            v-if="departmentRelation.department.hierarchyPath"
                            class="hierarchyPath"
                        >
                            ({{ departmentRelation.department.hierarchyPath }})
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(departmentRelation)">
                            <div
                                class="form-widget without-label date-picker"
                                :class="{ invalid: !isValidDateRange(editRelations[getEditIndex(departmentRelation)]) }"
                            >
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(departmentRelation)].start"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ moment(departmentRelation.start).format('DD.MM.YYYY') }}
                        </template>
                    </td>
                    <td class="kgr-table__cell">
                        <template v-if="isBeingEdited(departmentRelation)">
                            <div
                                class="form-widget without-label date-picker"
                                :class="{ invalid: !isValidDateRange(editRelations[getEditIndex(departmentRelation)]) }"
                            >
                                <kgr-date-picker
                                    v-model="editRelations[getEditIndex(departmentRelation)].end"
                                />
                            </div>
                        </template>
                        <template v-else>
                            {{ departmentRelation.formattedEnd() }}
                        </template>
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--actions">
                        <button
                            v-if="isRelationEditable(departmentRelation)"
                            type="button"
                            class="kgr-table__action"
                            :disabled="isBeingEdited(departmentRelation)"
                            @click="edit(departmentRelation)"
                        >
                            <kgr-icon-edit
                                :size="15"
                                :disabled="isBeingEdited(departmentRelation)"
                            />
                        </button>
                        <button
                            v-if="isRelationDeletable(departmentRelation)"
                            type="button"
                            class="kgr-table__action"
                            @click="remove(departmentRelation)"
                        >
                            <kgr-icon-delete />
                        </button>
                    </td>
                </tr>

                <tr
                    v-if="isCreatable"
                    class="kgr-table__row"
                >
                    <td class="kgr-table__cell">
                        <kgr-department-select
                            v-model="newRelation.department"
                            :club-id="clubId"
                            hide-label
                            exclude-if-basic-data-not-viewable
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newRelation.isEmpty() && (!newRelation.start || !isValidDateRange(newRelation)) }"
                        >
                            <kgr-date-picker v-model="newRelation.start" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
                        <div
                            class="form-widget without-label date-picker"
                            :class="{ invalid: !newRelation.isEmpty() && !isValidDateRange(newRelation) }"
                        >
                            <kgr-date-picker v-model="newRelation.end" />
                        </div>
                    </td>
                    <td class="kgr-table__cell">
&nbsp;
                    </td>
                </tr>
            </tbody>
        </table>
        <kgr-button
            v-if="isCreatable || isEditable"
            class="button_save"
            :value="$t('saveChanges')"
            :disabled="!isSavable"
            :pending="pendingCreate || pendingUpdate"
            @click="save()"
        />
    </div>
</template>

<script>
import DatePicker from '../common/form/picker/DatePicker';
import DepartmentMemberRelation from '../../js/dto/DepartmentMemberRelation';
import DepartmentSelect from '../common/form/select/DepartmentSelect';
import Error from '../../js/model/Error';
import IconDelete from '../common/icon/IconDelete';
import IconEdit from '../common/icon/IconEdit';
import KgrButton from '../common/form/button/KgrButton';
import MemberRepository from '../../js/repository/MemberRepository';

export default {
    name: 'KgrMemberDepartmentRelationsForm',
    components: {
        'kgr-department-select': DepartmentSelect,
        'kgr-icon-edit': IconEdit,
        'kgr-icon-delete': IconDelete,
        'kgr-button': KgrButton,
        'kgr-date-picker': DatePicker,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        departmentMemberRelations: {
            type: Array,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
        isEditable: {
            type: Boolean,
            required: true,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            newRelation: new DepartmentMemberRelation(),
            editRelations: [],
            pendingCreate: false,
            pendingUpdate: false,
        };
    },
    computed: {
        isSavable() {
            if (!this.isEditable && this.isCreatable) {
                return false;
            }

            if (this.editRelations.length === 0 && this.newRelation.isEmpty()) {
                return false;
            }

            const hasInvalidEdits = this.editRelations.filter((editRelation) => !this.isValidDateRange(editRelation)).length > 0;
            if (hasInvalidEdits) {
                return false;
            }

            if (!this.newRelation.isEmpty() && !this.newRelation.isValid()) {
                return false;
            }

            return true;
        },
    },
    methods: {
        isBeingEdited(departmentRelation) {
            return this.getEditIndex(departmentRelation) !== -1;
        },
        getEditIndex(departmentRelation) {
            return this.editRelations.map((editRelation) => editRelation.id).indexOf(departmentRelation.id);
        },
        isValidDateRange(departmentRelation) {
            if (!departmentRelation.end) {
                return true;
            }

            return this.moment(departmentRelation.end, 'DD.MM.YYYY') >= this.moment(departmentRelation.start, 'DD.MM.YYYY');
        },
        remove(departmentRelation) {
            showConfirm(this.$t('confirm'), this.$t('department.relation.confirm.delete', { name: departmentRelation.department.name }), () => {
                this.editRelations = this.editRelations.filter((r) => r.id !== departmentRelation.id);
                MemberRepository
                    .removeDepartment(this.memberId, departmentRelation.id)
                    .then(() => {
                        this.$notify.success('department.relation.delete.success', { name: departmentRelation.department.name });
                        this.emitUpdated();
                    })
                    .catch(() => {
                        this.$notify.error('saveChanges.error');
                    });
            }, 'Ok');
        },
        edit(departmentRelation) {
            if (this.isBeingEdited(departmentRelation)) {
                return;
            }

            this.editRelations.push(departmentRelation.toData());
        },
        save() {
            if (!this.newRelation.isEmpty()) {
                this.create();
            }

            if (this.editRelations.length > 0) {
                this.update();
            }
        },
        create() {
            this.pendingCreate = true;
            MemberRepository
                .createDepartmentRelation(this.memberId, this.newRelation)
                .then(() => {
                    this.newRelation = new DepartmentMemberRelation();
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch((error) => {
                    if (error.status === 409) {
                        this.$notify.error('department.relation.saveChanges.conflict', { name: this.newRelation.department.name });
                    } else {
                        this.$notify.error('saveChanges.error');
                    }
                })
                .finally(() => this.pendingCreate = false);
        },
        update() {
            this.pendingUpdate = true;
            MemberRepository
                .updateDepartmentRelations(this.memberId, this.editRelations)
                .then(() => {
                    this.editRelations = [];
                    this.$notify.success('saveChanges.success');
                    this.emitUpdated();
                })
                .catch(() => this.$notify.error('saveChanges.error'))
                .finally(() => this.pendingUpdate = false);
        },
        emitUpdated() {
            this.$emit('updated');
        },
        isRelationEditable(departmentMemberRelation) {
            return this.isEditable && departmentMemberRelation.isDepartmentViewable;
        },
        isRelationDeletable(departmentMemberRelation) {
            return this.isDeletable && departmentMemberRelation.isDepartmentViewable;
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
    .button_save {
    display: block;
    margin-left: auto;
}

div .date-picker {
    border: 1px solid transparent;

    &.invalid {
        border: 1px solid #f44336;
    }
}

.hierarchyPath {
    font-size: 80%;
}
</style>
